/* AgentActivityLog — 24-uurs logboek van alle agent-activiteiten.
 *
 * Twee exports:
 *  • AgentLogPanel  — tijdlijn voor ÉÉN agent (tab "Logboek" in de agent-modal)
 *  • ActivityLogModal — volledig logboek over alle agents (Orchestration-pagina)
 *
 * Data: GET /api/agents/activity?agent_id=&hours= — samengevoegde feed uit
 * agent_runs (runs) + audit_events met actor_type="agent" (acties).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { AGENTS } from "@/lib/agents";
import {
    Activity, X, Loader2, PlayCircle, Zap, AlertTriangle, Clock, RefreshCw,
} from "lucide-react";

const EXTRA_AGENTS = {
    verification_agent: { human_name: "Verificatie", name: "Verification" },
    pitch_agent: { human_name: "Pitch", name: "Pitch" },
    hunter: { human_name: "Hunter", name: "Hunter" },
};

const byId = Object.fromEntries(AGENTS.map((a) => [a.id, a]));

function agentInfo(id) {
    if (byId[id]) return byId[id];
    if (EXTRA_AGENTS[id]) return EXTRA_AGENTS[id];
    return { human_name: id?.length > 20 ? "Systeem" : (id || "?"), name: id };
}

function fmtTime(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const hm = d.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });
    return sameDay ? hm : `${d.toLocaleDateString("nl-BE", { day: "numeric", month: "short" })} ${hm}`;
}

const RANGES = [
    { hours: 24, label: "24u" },
    { hours: 168, label: "7d" },
    { hours: 720, label: "30d" },
];

function EntryRow({ e, idx, showAgent }) {
    const ag = agentInfo(e.agent_id);
    const failed = e.status === "failed";
    return (
        <li className="flex items-start gap-3 text-xs border-l-2 pl-3 py-1.5"
            style={{ borderColor: failed ? "var(--jr-danger, #ef4444)" : e.kind === "run" ? "var(--jr-brand)" : "rgba(255,255,255,0.12)" }}
            data-testid={`activity-entry-${idx}`}>
            {showAgent && (ag.photo_url ? (
                <img src={ag.photo_url} alt="" className="w-6 h-6 rounded-full object-cover bg-white/5 border border-white/10 shrink-0 mt-0.5" />
            ) : (
                <span className="w-6 h-6 rounded-full bg-white/8 grid place-items-center shrink-0 mt-0.5">
                    <Zap size={10} className="text-[var(--jr-text-3)]" />
                </span>
            ))}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    {showAgent && <span className="text-white font-medium truncate">{ag.human_name}</span>}
                    <span className={`text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-px rounded border ${e.kind === "run"
                        ? "bg-[var(--jr-brand)]/12 text-[var(--jr-brand)] border-[var(--jr-brand)]/25"
                        : "bg-white/5 text-[var(--jr-text-3)] border-white/10"}`}>
                        {e.kind === "run" ? "Run" : "Actie"}
                    </span>
                    {failed && (
                        <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-px rounded bg-[#ef4444]/12 text-[#ef4444] border border-[#ef4444]/25 inline-flex items-center gap-1">
                            <AlertTriangle size={8} /> Mislukt
                        </span>
                    )}
                    <span className="text-[10px] font-mono text-[var(--jr-text-3)] ml-auto shrink-0 inline-flex items-center gap-1">
                        <Clock size={9} /> {fmtTime(e.ts)}
                    </span>
                </div>
                <div className="text-[var(--jr-text-2)] mt-0.5 break-words">{e.summary || e.action || "—"}</div>
                {(e.tools_used || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                        {e.tools_used.map((t, i) => (
                            <span key={i} className="text-[9px] font-mono px-1.5 py-px rounded bg-white/5 text-[var(--jr-text-3)] border border-white/8">{t}</span>
                        ))}
                        {e.cost_credits > 0 && (
                            <span className="text-[9px] font-mono px-1.5 py-px rounded bg-[var(--jr-amber)]/10 text-[var(--jr-amber)] border border-[var(--jr-amber)]/20">{e.cost_credits}c</span>
                        )}
                    </div>
                )}
            </div>
        </li>
    );
}

function useActivity(agentId, hours) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        const params = new URLSearchParams({ hours: String(hours) });
        if (agentId) params.set("agent_id", agentId);
        api.get(`/agents/activity?${params}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ entries: [], stats: {}, total: 0 }))
            .finally(() => setLoading(false));
    };
    useEffect(load, [agentId, hours]); // eslint-disable-line react-hooks/exhaustive-deps
    return { data, loading, reload: load };
}

/* ------------------------------------------------------------------ */
/*  AgentLogPanel — tijdlijn voor één agent (tab in agent-modal)       */
/* ------------------------------------------------------------------ */
export function AgentLogPanel({ agentId }) {
    const [hours, setHours] = useState(24);
    const { data, loading } = useActivity(agentId, hours);
    const entries = data?.entries || [];
    const s = (data?.stats || {})[agentId] || { runs: 0, actions: 0, failed: 0, credits: 0 };
    return (
        <div data-testid="agent-log-panel">
            <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("agentlog.activity_last")}</span>
                {RANGES.map((r) => (
                    <button key={r.hours} onClick={() => setHours(r.hours)}
                        data-testid={`agent-log-range-${r.hours}`}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] border ${hours === r.hours
                            ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/40"
                            : "text-[var(--jr-text-3)] border-white/10 hover:text-white"}`}>
                        {r.label}
                    </button>
                ))}
                <span className="ml-auto text-[10px] font-mono text-[var(--jr-text-3)]">
                    {s.runs} runs · {s.actions} {i18n.t("agentlog.actions_word")}{s.failed > 0 ? ` · ${s.failed} ${i18n.t("agentlog.failed_word")}` : ""}{s.credits > 0 ? ` · ${s.credits}c` : ""}
                </span>
            </div>
            {loading ? (
                <div className="flex items-center gap-2 text-xs text-[var(--jr-text-3)] py-6 justify-center">
                    <Loader2 size={14} className="animate-spin" /> Logboek laden…
                </div>
            ) : entries.length === 0 ? (
                <div className="text-xs text-[var(--jr-text-3)] italic py-6 text-center" data-testid="agent-log-empty">
                    {i18n.t("agentlog.no_activity")}
                </div>
            ) : (
                <ul className="space-y-1" data-testid="agent-log-list">
                    {entries.map((e, i) => <EntryRow key={i} e={e} idx={i} showAgent={false} />)}
                </ul>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  ActivityLogModal — volledig logboek over alle agents               */
/* ------------------------------------------------------------------ */
export function ActivityLogModal({ onClose }) {
    const [hours, setHours] = useState(24);
    const [agentFilter, setAgentFilter] = useState("");
    const { data, loading, reload } = useActivity(agentFilter || null, hours);
    const entries = data?.entries || [];
    const stats = data?.stats || {};

    const agentChips = useMemo(() => {
        return Object.entries(stats)
            .map(([id, s]) => ({ id, ...agentInfo(id), total: (s.runs || 0) + (s.actions || 0), failed: s.failed || 0 }))
            .sort((a, b) => b.total - a.total);
    }, [stats]);

    const close = (e) => { if (e.target === e.currentTarget) onClose(); };
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={close}>
            <div className="w-full max-w-3xl max-h-[88vh] bg-[var(--jr-surface)] border border-white/10 rounded-2xl jr-fadein flex flex-col overflow-hidden"
                data-testid="activity-log-modal" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-white/8 shrink-0">
                    <div className="flex items-center gap-2">
                        <Activity size={16} className="text-[var(--jr-brand)]" />
                        <h2 className="font-display text-lg tracking-tight text-white">Agent-logboek</h2>
                        <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{data?.total ?? 0} activiteiten</span>
                        <div className="ml-auto flex items-center gap-1.5">
                            {RANGES.map((r) => (
                                <button key={r.hours} onClick={() => setHours(r.hours)}
                                    data-testid={`activity-log-range-${r.hours}`}
                                    className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] border ${hours === r.hours
                                        ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/40"
                                        : "text-[var(--jr-text-3)] border-white/10 hover:text-white"}`}>
                                    {r.label}
                                </button>
                            ))}
                            <button onClick={reload} data-testid="activity-log-refresh"
                                className="p-1.5 rounded-full text-[var(--jr-text-3)] hover:text-white border border-white/10">
                                <RefreshCw size={11} />
                            </button>
                            <button onClick={onClose} data-testid="activity-log-close"
                                className="p-1.5 rounded-full text-[var(--jr-text-2)] hover:text-white">
                                <X size={15} />
                            </button>
                        </div>
                    </div>
                    {/* Per-agent filter-chips met tellers */}
                    <div className="flex items-center gap-1.5 flex-wrap mt-3" data-testid="activity-log-chips">
                        <button onClick={() => setAgentFilter("")}
                            data-testid="activity-chip-all"
                            className={`px-2.5 py-1 rounded-full text-[10px] font-mono border ${!agentFilter
                                ? "bg-white/10 text-white border-white/25"
                                : "text-[var(--jr-text-3)] border-white/10 hover:text-white"}`}>
                            Alle agents
                        </button>
                        {agentChips.map((c) => (
                            <button key={c.id} onClick={() => setAgentFilter(agentFilter === c.id ? "" : c.id)}
                                data-testid={`activity-chip-${c.id}`}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-mono border ${agentFilter === c.id
                                    ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/40"
                                    : "text-[var(--jr-text-2)] border-white/10 hover:text-white"}`}>
                                {c.photo_url && <img src={c.photo_url} alt="" className="w-4 h-4 rounded-full object-cover" />}
                                {c.human_name} <span className="text-[var(--jr-text-3)]">{c.total}</span>
                                {c.failed > 0 && <span className="text-[#ef4444]">⚠{c.failed}</span>}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="p-5 flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center gap-2 text-xs text-[var(--jr-text-3)] py-8 justify-center">
                            <Loader2 size={14} className="animate-spin" /> Logboek laden…
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="text-xs text-[var(--jr-text-3)] italic py-8 text-center" data-testid="activity-log-empty">
                            <PlayCircle size={20} className="mx-auto mb-2 opacity-40" />
                            Geen agent-activiteit in deze periode.
                        </div>
                    ) : (
                        <ul className="space-y-1" data-testid="activity-log-list">
                            {entries.map((e, i) => <EntryRow key={i} e={e} idx={i} showAgent />)}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
